import { useMemo, useState } from 'react';

import {
  Alert,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  HorizontalTabsList,
  Icons,
  Tabs,
  TabsContent,
  TabsTrigger,
} from '@commenda/kakashi';

import {
  IIndirectTaxFiling,
  IndirectTaxFilingsStatusRowSource,
} from '../types';
import { IndirectTaxFilingsStatusBreakdownTable } from './IndirectTaxFilingsStatusBreakdownTable';
import { IndirectTaxFilingsStatusContributionTable } from './IndirectTaxFilingsStatusContributionTable';
import { JobErrorsModal } from './JobErrorsModal';
import { useIndirectTaxFilingsStatusModal } from './useIndirectTaxFilingsStatusModal';

interface IndirectTaxFilingsStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  corporationId: number;
  /**
   * The in-scope filing list from the dashboard. The hook splits this list
   * by region itself — caller doesn't need to pre-filter.
   */
  filings: IIndirectTaxFiling[];
  /**
   * Optional row-click handler — when provided, table rows on both tabs
   * become clickable and navigate the user to the filing detail page. The
   * modal closes itself before invoking the callback so the user lands on
   * the filing page without the modal still mounted on top.
   */
  onFilingClick?: (filingId: string) => void;
  /**
   * Admin-only. When true, rows with failures expose a "View error" button that
   * opens the underlying job errors. Only the admin app (Filing Conductor)
   * passes this — customer apps omit it so the action never shows there.
   */
  showJobErrorActions?: boolean;

  onAnalysisRun?: (
    source: IndirectTaxFilingsStatusRowSource,
    label: string
  ) => void;
}

enum IndirectTaxFilingsStatusTab {
  BREAKDOWN = 'breakdown',
  CONTRIBUTION = 'contribution',
}

const IndirectTaxFilingsStatusModal = ({
  isOpen,
  onClose,
  corporationId,
  filings,
  onFilingClick,
  showJobErrorActions = false,
  onAnalysisRun,
}: IndirectTaxFilingsStatusModalProps) => {
  const {
    isLoading,
    error,
    hasBreakdownFilings,
    hasContributionFilings,
    hasAnyFilings,
    breakdownFilingCount,
    contributionFilingCount,
    breakdownDisplayRows,
    contributionDisplayRows,
    refetch,
    isBreakdownTriggering,
    isContributionTriggering,
    isAggregateSyncTriggering,
    triggerBreakdownGeneration,
    triggerContributionSync,
    triggerAggregateSync,
    triggerError,
  } = useIndirectTaxFilingsStatusModal({ isOpen, corporationId, filings });

  const handleRecheckStatus = () => {
    void refetch();
  };
  const handleRerunBreakdownGeneration = () => {
    void triggerBreakdownGeneration();
  };
  const handleRerunContributionSync = () => {
    void triggerContributionSync();
  };
  const handleSyncAggregates = () => {
    void triggerAggregateSync();
  };
  const handleFilingClick = onFilingClick
    ? (filingId: string) => {
        // Close the modal before navigating so the user lands on a clean
        // filing page (the modal would otherwise stay mounted over it).
        onClose();
        onFilingClick(filingId);
      }
    : undefined;

  const [activeStatusTab, setActiveStatusTab] =
    useState<IndirectTaxFilingsStatusTab>(
      IndirectTaxFilingsStatusTab.BREAKDOWN
    );

  // Admin-only: the failed status row currently being inspected in the job-
  // errors modal. null when closed.
  const [jobErrors, setJobErrors] = useState<{
    source: IndirectTaxFilingsStatusRowSource;
    label: string;
  } | null>(null);
  const handleViewBreakdownErrors = (filingId: string, label: string) =>
    setJobErrors({ source: { type: 'filing', filingId }, label });
  const handleViewJobErrors = (jobId: string, label: string) =>
    setJobErrors({ source: { type: 'job', jobId }, label });
  const onBreakdownAnalysisRun = onAnalysisRun
    ? (filingId: string, label: string) =>
        onAnalysisRun({ type: 'filing', filingId }, label)
    : undefined;

  // Auto-select a non-empty tab if the user's choice has no rows. Derived
  // via useMemo (not useEffect → setState) so it updates with the bucket
  // counts without an extra render cycle.
  const effectiveActiveStatusTab = useMemo<IndirectTaxFilingsStatusTab>(() => {
    if (
      activeStatusTab === IndirectTaxFilingsStatusTab.BREAKDOWN &&
      !hasBreakdownFilings &&
      hasContributionFilings
    ) {
      return IndirectTaxFilingsStatusTab.CONTRIBUTION;
    }
    if (
      activeStatusTab === IndirectTaxFilingsStatusTab.CONTRIBUTION &&
      !hasContributionFilings &&
      hasBreakdownFilings
    ) {
      return IndirectTaxFilingsStatusTab.BREAKDOWN;
    }
    return activeStatusTab;
  }, [activeStatusTab, hasBreakdownFilings, hasContributionFilings]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="bg-white min-w-[1000px] max-w-[90vw] flex flex-col h-[800px] max-h-[80vh] p-none overflow-hidden"
        preventCloseOnOutsideClick
      >
        <DialogHeader>
          <DialogTitle>Tax liability report status</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3xl p-3xl overflow-y-auto flex-1">
          {error && (
            <Alert
              variant="destructive"
              title="Failed to load status"
              description={error}
              icon={<Icons.AlertCircle className="size-5 text-text-error" />}
              action={{
                label: 'Retry',
                onClick: handleRecheckStatus,
                variant: 'secondary',
              }}
            />
          )}

          {triggerError && (
            <Alert
              variant="destructive"
              title="Failed to trigger run"
              description={triggerError}
              icon={<Icons.AlertCircle className="size-5 text-text-error" />}
            />
          )}

          {!hasAnyFilings && !error && (
            <Alert
              variant="warning"
              title="No filings in scope"
              description="Adjust the period filter on the dashboard to include filings, then re-open this dialog."
              icon={<Icons.AlertCircle className="size-5 text-text-warning" />}
            />
          )}

          {hasAnyFilings && (
            <Tabs
              value={effectiveActiveStatusTab}
              // Cast is safe: TabsTrigger value is one of two enum members
              // hardcoded below; no other value can reach this callback.
              onValueChange={(value) =>
                setActiveStatusTab(value as IndirectTaxFilingsStatusTab)
              }
            >
              {/*
                Standalone "Refresh" button removed — each tab now hosts its
                own banner with re-check + re-run actions, matching the
                legacy sales-tax modal's pattern.
              */}
              <div className="flex items-center justify-between mb-xl">
                <HorizontalTabsList variant="underline" size="md">
                  <TabsTrigger
                    value={IndirectTaxFilingsStatusTab.BREAKDOWN}
                    disabled={!hasBreakdownFilings}
                  >
                    {`United States & Canada (${breakdownFilingCount})`}
                  </TabsTrigger>
                  <TabsTrigger
                    value={IndirectTaxFilingsStatusTab.CONTRIBUTION}
                    disabled={!hasContributionFilings}
                  >
                    {`European Union & United Kingdom (${contributionFilingCount})`}
                  </TabsTrigger>
                </HorizontalTabsList>
              </div>

              <TabsContent value={IndirectTaxFilingsStatusTab.BREAKDOWN}>
                <IndirectTaxFilingsStatusBreakdownTable
                  rows={breakdownDisplayRows}
                  filingCount={breakdownFilingCount}
                  isLoading={isLoading}
                  isRerunInProgress={isBreakdownTriggering}
                  isAggregateSyncInProgress={isAggregateSyncTriggering}
                  onRecheckStatus={handleRecheckStatus}
                  onRerunBreakdownGeneration={handleRerunBreakdownGeneration}
                  onSyncAggregates={handleSyncAggregates}
                  onFilingClick={handleFilingClick}
                  showJobErrorActions={showJobErrorActions}
                  onViewBreakdownErrors={handleViewBreakdownErrors}
                  onBreakdownAnalysisRun={onBreakdownAnalysisRun}
                />
              </TabsContent>

              <TabsContent value={IndirectTaxFilingsStatusTab.CONTRIBUTION}>
                <IndirectTaxFilingsStatusContributionTable
                  rows={contributionDisplayRows}
                  filingCount={contributionFilingCount}
                  isLoading={isLoading}
                  isRerunInProgress={isContributionTriggering}
                  onRecheckStatus={handleRecheckStatus}
                  onRerunContributionSync={handleRerunContributionSync}
                  onFilingClick={handleFilingClick}
                  showJobErrorActions={showJobErrorActions}
                  onViewJobErrors={handleViewJobErrors}
                />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </DialogContent>

      {showJobErrorActions ? (
        <JobErrorsModal
          isOpen={jobErrors !== null}
          onClose={() => setJobErrors(null)}
          corporationId={corporationId}
          label={jobErrors?.label}
          source={jobErrors?.source ?? null}
        />
      ) : null}
    </Dialog>
  );
};

export { IndirectTaxFilingsStatusModal };
